import { Box, Button, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { AppConstantsContext } from '../../../context';
import {
  getViewDuration,
  TrackingAction,
  TrackingCategory,
  trackingService,
} from '../../../services';
import FeatureList from './FeatureList';
import Price from './Price';
import { setUrl } from './Pricing.config';
import PricingCardOption from './PricingCardOption';
import SalesBorderWrapper from './SalesBorderWrapper';
import type { PriceData } from './usePricing';
import type { SaleEventProps } from './useSaleEvent';
import InputContainerModal from '../InputContainer/InputContainerModal';

interface PricingCardProps {
  title: string;
  cardType: 'free' | 'single' | 'pro' | 'universe' | 'monthly' | 'yearly';
  highlighted?: boolean;
  transcriptionId?: string;
  matchingSaleEvent?: SaleEventProps;
  priceOptions: PriceData[];
  wordpress?: boolean;
  redirectUrl?: string;
}

interface StyleProps {
  highlighted: boolean;
  additional: any;
  cardType: any;
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  card: {
    position: 'relative',
    border: '1px solid #5b5b5f8a',
    boxShadow: props.highlighted ? `0px 0px 10px 10px ${theme.palette.primary.main}` : undefined,
    borderRadius: 12,
    background: props.highlighted ? '#303031' : '#252526',
    textAlign: 'left',
    padding: '18px 15px',
    minWidth: '20em',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '36em',
    minHeight: props.cardType === 'universe' || props.cardType === 'pro' ? '28em' : 'auto',
    margin: props.highlighted ? '1.1em 0 1.4em' : '1.1em 0',

    [theme.breakpoints.down('md')]: {
      padding: '15px 10px',
      minHeight: 'inherit',
      gap: 10,
    },
    ...(props.highlighted && props.additional?.card),
  },
  productTitle: {
    position: 'relative',
    marginBottom: 10,
    justifyContent: 'space-between',
    display: 'flex',

    ...(props.additional?.productTitle),
  },
  buttonWrapper: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    ...props.additional?.button,
  },
  cancelAnytime: {
    marginTop: 10,
    textAlign: 'center',
    fontSize: '0.8em',
    fontStyle: 'italic',
    color: '#ffffff8a',
  },
  container: {
    [theme.breakpoints.down('md')]: {
      width: '80%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  bestValue: {
    position: 'absolute',
    top: -10,
    right: -1,
    padding: '2px 11px',
    fontSize: '0.9rem',
    fontWeight: 600,
    background: '#303031',
    color: theme.palette.primary.main,
    zIndex: 2,
    borderColor: '#48484b',
    borderTop: '1px solid #48484b',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderRight: '1px solid #48484b',
    ...props.additional?.bestValue,
  },
  bestValueColor: {
    filter: 'brightness(1.2)',
  },
}));

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  cardType,
  wordpress,
  highlighted = false,
  transcriptionId,
  matchingSaleEvent,
  priceOptions,
  redirectUrl,
}) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<string | null>('yearly');
  const { classes } = useStyles({ highlighted, additional: matchingSaleEvent?.styles, cardType });
  const { jobType } = useContext(AppConstantsContext);
  const [openInputContainer, setOpenInputContainer] = useState(false);

  const handleClickButton = () => {
    if (wordpress) {
      window.location.href = `${window.location.href}#cta`;
      return;
    }
    if (cardType === 'free' && !wordpress) {
      setOpenInputContainer(true);
      return;
    }
    let productName = 'single';
    if (cardType === 'pro' || cardType === 'universe') {
      productName = `${cardType}_${selectedOption}`; // pro_monthly or pro_yearly or universe_monthly or universe_yearly
    } else if (cardType === 'monthly' || cardType === 'yearly') {
      productName = `pro_${cardType}`; // monthly or yearly
    }
    trackingService.valueObjectEvent<{
      app_name: string;
      product_name: string;
      duration?: number;
    }>(TrackingCategory.Checkout, TrackingAction.ACTION_CLICK_CHECKOUT, {
      app_name: jobType,
      product_name: productName,
      duration: getViewDuration((window as any).pricingTableStartTime),
    });
    console.log(
      TrackingAction.ACTION_CLICK_CHECKOUT,
      getViewDuration((window as any).pricingTableStartTime)
    );

    const baseUrl =
      cardType === 'single' ? 'https://auth.klang.io/buy-single' : 'https://auth.klang.io/buy';
    const priceData =
      priceOptions.length === 1 || selectedOption === 'monthly' ? priceOptions[0] : priceOptions[1];

    const redirectUrlValue = redirectUrl ?? (cardType !== 'single' ? window.location.href : null);

    const href = setUrl({
      baseUrl,
      productId: priceData.productId.toString(),
      redirectUrl: redirectUrlValue,
      transcriptionId,
      code: priceData.hasDiscount ? matchingSaleEvent?.discountData?.code : undefined,
    });

    window.location.href = href;
  };

  let radioOptions = null;
  if (priceOptions?.length > 1) {
    radioOptions = [
      {
        billingCycle: 'monthly',
        label: t('home.pricing.billed_monthly'),
        price: priceOptions[0]?.price,
        oldPrice: priceOptions[0]?.oldPrice,
        hasDiscount: priceOptions[0]?.hasDiscount,
      },
      {
        billingCycle: 'yearly',
        label: t('home.pricing.billed_yearly'),
        price: priceOptions[1]?.price,
        oldPrice: priceOptions[1]?.oldPrice,
        hasDiscount: priceOptions[1]?.hasDiscount,
      },
    ];
  }
  return (
    <div className={classes.container}>
      <Typography variant="h6" className={classes.productTitle}>
        {title}
      </Typography>
      <Box className={classes.card}>
        {(cardType === 'pro' || cardType === 'yearly') && (
          <span className={classes.bestValue}>
            <span className={classes.bestValueColor}>{t('home.pricing.best_value')}</span>
          </span>
        )}
        <FeatureList cardType={cardType} />
        {cardType === 'free' && <Price price={t('home.pricing.free')} />}
        {priceOptions?.length === 1 ? (
          <SalesBorderWrapper
            selected={cardType === 'yearly'}
            matchingSaleEvent={matchingSaleEvent}
            hide={!matchingSaleEvent}
            withRadioSelection={false}>
            <Price
              matchingSaleEvent={matchingSaleEvent}
              price={priceOptions?.[0]?.price}
              oldPrice={priceOptions?.[0]?.oldPrice}
              billingCycle={cardType === 'monthly' || cardType === 'yearly' ? cardType : 'single'}
            />
          </SalesBorderWrapper>
        ) : (
          <div>
            {radioOptions?.map((option, index) => (
              <PricingCardOption
                // eslint-disable-next-line react/no-array-index-key
                key={cardType + index}
                cardType={cardType as 'pro' | 'universe'}
                matchingSaleEvent={option.hasDiscount ? matchingSaleEvent : null}
                setSelectedOption={setSelectedOption}
                billingCycle={option.billingCycle as 'monthly' | 'yearly'}
                label={option.label}
                price={option.price}
                oldPrice={option?.oldPrice}
                selected={selectedOption === option.billingCycle}
              />
            ))}
          </div>
        )}
        <div className={classes.buttonWrapper}>
          <Button
            id={`btn_pricing_${cardType}`}
            className={classes.button}
            onClick={handleClickButton}
            variant={highlighted ? 'contained' : 'outlined'}>
            {cardType === 'free' ? t('home.pricing.try_it_out') : 'UPGRADE'}
          </Button>
        </div>
      </Box>
      {priceOptions?.[0]?.isRecurring && (
        <Typography className={classes.cancelAnytime}>
          {t('home.pricing.cancel_anytime')}
        </Typography>
      )}
      {openInputContainer && (
        <InputContainerModal handleClose={() => setOpenInputContainer(false)} />
      )}
    </div>
  );
};

export default PricingCard;
